import {
  cloneElement,
  isValidElement,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react";
import type { TooltipContentProps, TooltipValueType } from "recharts";

const CURSOR_TOOLTIP_GAP = 12;
const EDGE_MARGIN = 8;

/** minmax(3.5rem, 1fr) per format column in the matrix tooltip. */
export const FORMAT_TOOLTIP_COLUMN_WIDTH = 56;
const FORMAT_TOOLTIP_BASE_WIDTH = 96;

export interface CursorTooltipOptions {
  estimatedWidth?: number;
}

/** `transform: none` on the wrapper stops unclamped recharts boxes from growing scrollbars. */
export const cursorTooltipWrapperProps: {
  allowEscapeViewBox: { x: boolean; y: boolean };
  wrapperStyle: CSSProperties;
  offset: number;
} = {
  allowEscapeViewBox: { x: true, y: true },
  wrapperStyle: { overflow: "visible", zIndex: 10, transform: "none" },
  offset: 0,
};

export function estimateFormatTooltipWidth(formatCount: number): number {
  return FORMAT_TOOLTIP_BASE_WIDTH + formatCount * FORMAT_TOOLTIP_COLUMN_WIDTH;
}

export interface ClampBand {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/** Always above the anchor; pinned to band top when it cannot fit (never flipped below). */
export function placeTooltip({
  anchor,
  size,
  band,
}: {
  anchor: { x: number; y: number };
  size: { width: number; height: number };
  band: ClampBand;
}): { dx: number; dy: number } {
  const minLeft = band.left + EDGE_MARGIN;
  const maxLeft = band.right - EDGE_MARGIN - size.width;
  const left = Math.max(minLeft, Math.min(anchor.x - size.width / 2, maxLeft));

  const above = anchor.y - size.height - CURSOR_TOOLTIP_GAP;
  const top = Math.max(band.top + EDGE_MARGIN, above);

  return { dx: left - anchor.x, dy: top - anchor.y };
}

function clipAncestorsRect(
  start: Element,
): { left: number; right: number; top: number; bottom: number } | null {
  let rect: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  } | null = null;
  for (let node = start.parentElement; node; node = node.parentElement) {
    const { overflowX, overflowY } = getComputedStyle(node);
    if (overflowX !== "visible" || overflowY !== "visible") {
      const box = node.getBoundingClientRect();
      rect = rect
        ? {
            left: Math.max(rect.left, box.left),
            right: Math.min(rect.right, box.right),
            top: Math.max(rect.top, box.top),
            bottom: Math.min(rect.bottom, box.bottom),
          }
        : {
            left: box.left,
            right: box.right,
            top: box.top,
            bottom: box.bottom,
          };
    }
  }
  return rect;
}

// Unbounded first pass so placement centres before measure.
const UNBOUNDED_BAND: ClampBand = {
  left: -1e6,
  right: 1e6,
  top: -1e6,
  bottom: 1e6,
};

/** Recharts 3 passes no viewBox width to tooltip content; measure on layout instead. */
function CursorTooltipContent({
  children,
  anchor,
  estimatedWidth,
}: {
  children: ReactNode;
  anchor: { x: number; y: number };
  estimatedWidth: number;
}) {
  "use no memo";

  const ref = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState<{
    size: { width: number; height: number };
    band: ClampBand;
  }>(() => ({
    size: { width: estimatedWidth, height: 0 },
    band: UNBOUNDED_BAND,
  }));

  // Re-runs each hover; setMeasured no-op guard avoids a loop with empty deps.
  useLayoutEffect(() => {
    const element = ref.current;
    const chartWrapper = element?.closest(".recharts-wrapper");
    if (!element || !chartWrapper) {
      return;
    }

    const tooltipRect = element.getBoundingClientRect();
    const chartRect = chartWrapper.getBoundingClientRect();
    const clipRect = clipAncestorsRect(chartWrapper);

    const band: ClampBand = {
      left: Math.max(clipRect?.left ?? 0, 0) - chartRect.left,
      right:
        Math.min(clipRect?.right ?? window.innerWidth, window.innerWidth) -
        chartRect.left,
      top: Math.max(clipRect?.top ?? 0, 0) - chartRect.top,
      bottom:
        Math.min(clipRect?.bottom ?? window.innerHeight, window.innerHeight) -
        chartRect.top,
    };
    const size = { width: tooltipRect.width, height: tooltipRect.height };

    setMeasured((current) =>
      current.size.width === size.width &&
      current.size.height === size.height &&
      current.band.left === band.left &&
      current.band.right === band.right &&
      current.band.top === band.top &&
      current.band.bottom === band.bottom
        ? current
        : { size, band },
    );
  });

  const { dx, dy } = placeTooltip({
    anchor,
    size: measured.size,
    band: measured.band,
  });

  return (
    // Absolute + w-max: pinned wrapper stays 0x0; only this box has bounds.
    <div
      ref={ref}
      className="pointer-events-none absolute top-0 left-0 w-max"
      style={{
        transform: `translate(${anchor.x + dx}px, ${anchor.y + dy}px)`,
        transition: "transform 400ms ease",
      }}
    >
      {children}
    </div>
  );
}

export const renderCursorTooltip = (
  tooltipElement: ReactElement,
  props: TooltipContentProps<TooltipValueType, string | number>,
  options?: CursorTooltipOptions,
): ReactElement | null => {
  const { active, coordinate, ...rest } = props;

  if (!active || !isValidElement(tooltipElement)) {
    return null;
  }

  const rendered = cloneElement(tooltipElement, {
    active,
    coordinate,
    ...rest,
  } as Partial<TooltipContentProps<TooltipValueType, string | number>>);

  if (!coordinate) {
    return rendered;
  }

  return (
    <CursorTooltipContent
      anchor={{ x: coordinate.x ?? 0, y: coordinate.y ?? 0 }}
      estimatedWidth={options?.estimatedWidth ?? 0}
    >
      {rendered}
    </CursorTooltipContent>
  );
};
